import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Task } from '@types';
import { environment } from 'environments/environment';
import {
	BehaviorSubject,
	filter,
	map,
	type Observable,
	switchMap,
	take,
	tap,
} from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class TasksService {
	httpClient = inject(HttpClient);

	apiUrl = environment.url;
	// Camunda URL removed - using backend API instead

	private readonly task: BehaviorSubject<Task | null> = new BehaviorSubject(
		null,
	);
	private readonly tasks: BehaviorSubject<Task[] | null> =
		new BehaviorSubject(null);


	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Acessor de Task
	 */
	get task$(): Observable<Task> {
		return this.task.asObservable();
	}

	/**
	 * Acessor de Tasks
	 */
	get tasks$(): Observable<Task[]> {
		return this.tasks.asObservable();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Get tasks
	 */
	getTasks(): Observable<Task[]> {
		let headers = new HttpHeaders();
		headers = headers.append('Content-Type', 'application/json ');
		headers = headers.append('Access-Control-Allow-Origin', '*');

		// api/task
		return this.httpClient
			.get<Task[]>(this.apiUrl + 'task', {
				headers: headers,
				params: { type: 'tasks' },
			})
			.pipe(
				tap((response) => {
					this.tasks.next(response);
				}),
			);
	}

	/**
	 * Update tasks orders
	 *
	 * @param tasks
	 */
	updateTasksOrders(tasks: Task[]): Observable<Task[]> {
		return this.httpClient.patch<Task[]>('api/apps/tasks/order', { tasks });
	}

	/**
	 * Search tasks with given query
	 *
	 * @param query
	 */
	searchTasks(query: string): Observable<Task[] | null> {
		return this.httpClient.get<Task[] | null>('api/apps/tasks/search', {
			params: { query },
		});
	}

	/**
	 * Create task
	 *
	 * @param type
	 */
	createTask(type: string): Observable<Task> {
		return this.tasks$.pipe(
			take(1),
			switchMap((tasks) =>
				this.httpClient
					.post<Task>('api/apps/tasks/task', { type })
					.pipe(
						map((newTask) => {
							// Update the tasks with the new task
							this.tasks.next([newTask, ...tasks]);

							// Return the new task
							return newTask;
						}),
					),
			),
		);
	}

	/**
	 * Start Task
	 *
	 * @description start a new task
	 * @param {*} [data] data to start a new task
	 * @return {*}  {Observable<any>} return the new task
	 */
	startTask(data?: any): Observable<any> {
		return this.httpClient.post<any>(this.apiUrl + 'task', { data });
	}
	/**
	 * ### Complete Task
	 * @description Complete a task through the backend API
	 * @param {string} taskID the ID of the task to be completed
	 * @param {{}} [action] action to be executed
	 * @return {*}  {Observable<any>} returns the response from the request
	 */
	completeTask(taskID: string, action?: {}): Observable<any> {
		return this.httpClient
			.post<any>(this.apiUrl + 'task/' + taskID + '/complete', { action })
			.pipe(tap((response) => {}));
	}

	/**
	 * Complete task
	 *
	 * @param taskid
	 */
	completeTaskOLD(taskid: string, action: string): Observable<any> {
		return this.httpClient
			.post<any>(
				'http://localhost:8081/engine-rest/task/' +
					taskid +
					'/complete',
				{
					variables: {
						action: {
							value: action,
						},
					},
					withVariablesInReturn: true,
				},
			)
			.pipe(
				map((newTask) => {
					// Update the tasks with the new task
					this.tasks.next([newTask]);

					// Return the new task
					return newTask;
				}),
			);
	}

	/**
	 * Update task
	 *
	 * @param id
	 * @param task
	 */
	updateTask(id: string, task: Task): Observable<Task> {
		return this.tasks$.pipe(
			take(1),
			switchMap((tasks) =>
				this.httpClient
					.patch<Task>('api/apps/tasks/task', {
						id,
						task,
					})
					.pipe(
						map((updatedTask) => {
							// Find the index of the updated task
							const index = tasks.findIndex(
								(item) => item.id === id,
							);

							// Update the task
							tasks[index] = updatedTask;

							// Update the tasks
							this.tasks.next(tasks);

							// Return the updated task
							return updatedTask;
						}),
						switchMap((updatedTask) =>
							this.task$.pipe(
								take(1),
								filter((item) => item && item.id === id),
								tap(() => {
									// Update the task if it's selected
									this.task.next(updatedTask);

									// Return the updated task
									return updatedTask;
								}),
							),
						),
					),
			),
		);
	}

	/**
	 * Delete the task
	 *
	 * @param id
	 */
	deleteTask(id: string): Observable<boolean> {
		return this.tasks$.pipe(
			take(1),
			switchMap((tasks) =>
				this.httpClient
					.delete('api/apps/tasks/task', { params: { id } })
					.pipe(
						map((isDeleted: boolean) => {
							// Find the index of the deleted task
							const index = tasks.findIndex(
								(item) => item.id === id,
							);

							// Delete the task
							tasks.splice(index, 1);

							// Update the tasks
							this.tasks.next(tasks);

							// Return the deleted status
							return isDeleted;
						}),
					),
			),
		);
	}
}
